/** GPU Hue/Saturation — fragment-shader port of
 *  `lib/adjustments/hue-saturation.ts`.
 *
 *  Master-mode only (matches the JS path's v1 scope). Per-pixel
 *  RGB → HSL → shift → RGB via the shared helpers in
 *  `../glsl-helpers.ts`. The shifts themselves mirror the JS
 *  branches exactly so byte parity is structural:
 *
 *  - hue:    `mod(mod(hu + shift, 360) + 360, 360)`
 *  - sat:    positive → `sa + (1 - sa) * shift`,
 *            negative → `sa * (1 + shift)`.
 *  - light:  same shape as sat. */

import { RGB_TO_HSL_GLSL, HSL_TO_RGB_GLSL } from '../glsl-helpers';
import type { HueSaturationValue } from '../../adjustments/hue-saturation';
import { applyShader } from '../runtime';

const HUE_SATURATION_FRAG = `#version 300 es
precision highp float;
in vec2 v_uv;
uniform sampler2D u_source;
uniform float u_hueShift;      // degrees, raw value (-180..180)
uniform float u_satShift;      // normalized [-1, 1] (raw / 100)
uniform float u_lightShift;    // normalized [-1, 1] (raw / 100)
out vec4 outColor;
${RGB_TO_HSL_GLSL}
${HSL_TO_RGB_GLSL}

void main() {
  vec4 c = texture(u_source, v_uv);
  vec3 hsl = rgbToHsl(c.rgb);
  // Hue: shift, wrap into [0, 360). Matches JS double-mod wraparound.
  hsl.x = mod(mod(hsl.x + u_hueShift, 360.0) + 360.0, 360.0);
  // Saturation: asymmetric (positive blends toward 1, negative scales toward 0).
  if (u_satShift >= 0.0) hsl.y = hsl.y + (1.0 - hsl.y) * u_satShift;
  else hsl.y = hsl.y * (1.0 + u_satShift);
  // Lightness: same shape as saturation.
  if (u_lightShift >= 0.0) hsl.z = hsl.z + (1.0 - hsl.z) * u_lightShift;
  else hsl.z = hsl.z * (1.0 + u_lightShift);
  vec3 rgb = hslToRgb(hsl);
  // Match the JS final round + clamp.
  rgb = clamp(floor(rgb * 255.0 + 0.5) / 255.0, 0.0, 1.0);
  outColor = vec4(rgb, c.a);
}
`;

export function applyHueSaturationOnGpu(
  source: ImageData,
  target: HTMLCanvasElement,
  value: HueSaturationValue,
): boolean {
  return applyShader({
    source,
    target,
    fragShaderSrc: HUE_SATURATION_FRAG,
    uniforms: {
      u_hueShift: { type: 'float', value: value.hue },
      u_satShift: { type: 'float', value: value.saturation / 100 },
      u_lightShift: { type: 'float', value: value.lightness / 100 },
    },
  });
}
